import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
    Users, 
    Shield,
    ShieldOff,
    Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useDocsI18n } from '../../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminUsers({ 
    users,
    currentUser,
    token,
    onDelete,
    onDataRefresh
}) {
    const { t } = useDocsI18n();
    const toggleAdmin = async (u) => {
        try {
            await axios.put(`${API}/users/${u.id}/admin?is_admin=${!u.is_admin}`);
            toast.success(u.is_admin ? 'Admin-Rechte entzogen' : 'Zum Admin gemacht');
            onDataRefresh?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('changeFailed'));
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-mono font-bold">{t('usersTitle')}</h1>
                <Badge variant="outline" className="font-mono">
                    {users.length} Benutzer
                </Badge>
            </div>

            <div className="space-y-2">
                {users.map(u => (
                    <div
                        key={u.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-card/30 hover:bg-card/50 transition-colors group"
                    >
                        <div className="flex items-center gap-4">
                            {u.avatar ? (
                                <img
                                    src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.avatar}.png`}
                                    alt={u.username}
                                    className="w-10 h-10 rounded-full"
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground">
                                    {u.username[0].toUpperCase()}
                                </div>
                            )}
                            <div>
                                <h3 className="font-mono font-bold flex items-center gap-2 flex-wrap">
                                    {u.username}
                                    {u.is_super_admin && (
                                        <Badge className="text-xs bg-yellow-500/20 text-yellow-500 border-yellow-500/50">
                                            <Shield className="w-3 h-3 mr-1" />
                                            Super-Admin
                                        </Badge>
                                    )}
                                    {u.is_admin && !u.is_super_admin && (
                                        <Badge className="text-xs bg-primary/20 text-primary">
                                            <Shield className="w-3 h-3 mr-1" />
                                            Admin
                                        </Badge>
                                    )}
                                </h3>
                                <p className="text-xs text-muted-foreground">
                                    Discord ID: {u.discord_id}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {u.id !== currentUser.id && !u.is_super_admin && (
                                <>
                                    <Button
                                        variant={u.is_admin ? "destructive" : "default"}
                                        size="sm"
                                        onClick={() => toggleAdmin(u)}
                                    >
                                        {u.is_admin ? (
                                            <>
                                                <ShieldOff className="w-4 h-4 mr-1" />
                                                Rechte entziehen
                                            </>
                                        ) : (
                                            <>
                                                <Shield className="w-4 h-4 mr-1" />
                                                Zum Admin
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => onDelete(u)}
                                    >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
                {users.length === 0 && (
                    <div className="text-center py-12 border border-dashed border-border rounded-xl">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">{t('noUsers')}</p>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
